import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@material-ui/core';
import { LANDING_URL } from '../../config';
import {
  consentUiEnabled,
  onAnalyticsChange,
  onConsentReviewRequested,
  readConsent,
  recordConsent
} from '../../services/analyticsService';
import type { ConsentChoice } from '../../services/analyticsConsent';
import './ConsentBanner.scss';

/**
 * Asks once whether analytics and advertising cookies may be used. Consent is strict: until the customer accepts,
 * no analytics script is loaded and nothing is sent; accepting loads it there and then, rejecting
 * (or withdrawing later) means it is not there at all. See src/services/analyticsService.ts.
 *
 * Not a dialog, on purpose: it never traps focus or blocks the page, and one click answers it.
 * The only trace is a visually hidden status message, so a screen-reader user hears that the
 * choice was saved. The footer's "Cookie settings" brings the banner back, so a choice can be
 * withdrawn as easily as it was given. Same wording and same cookie as the marketing site.
 *
 * Rendered wherever a container is configured (live), and on staging and local builds too so it
 * can be tested: there it stores the same choice, with no container to load and nothing sent.
 */
const ConsentBanner: React.FC = () => {
  const [enabled, setEnabled] = useState(() => consentUiEnabled());
  const [choice, setChoice] = useState<ConsentChoice | undefined>(() => readConsent());
  const [reviewing, setReviewing] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const headingRef = useRef<HTMLHeadingElement>(null);
  const bannerRef = useRef<HTMLElement>(null);

  useEffect(() => onConsentReviewRequested(() => setReviewing(true)), []);
  useEffect(
    () =>
      onAnalyticsChange(() => {
        setEnabled(consentUiEnabled());
        setChoice(readConsent());
      }),
    []
  );

  // Reopened from the footer: take the customer to the question.
  useEffect(() => {
    if (reviewing) {
      headingRef.current?.focus();
    }
  }, [reviewing]);

  const visible = enabled && (choice === undefined || reviewing);

  // The banner is fixed over the bottom of the viewport, so while it shows the page reserves that
  // much room at its end (see ConsentBanner.scss): the footer can always be scrolled clear of it.
  useEffect(() => {
    const banner = bannerRef.current;
    if (!visible || !banner) {
      return undefined;
    }

    const root = document.documentElement;
    const reserve = () => root.style.setProperty('--consent-banner-height', `${banner.offsetHeight}px`);
    reserve();
    window.addEventListener('resize', reserve);
    return () => {
      window.removeEventListener('resize', reserve);
      root.style.removeProperty('--consent-banner-height');
    };
  }, [visible]);

  if (!enabled) {
    return null;
  }

  const decide = (next: ConsentChoice) => {
    recordConsent(next);
    setChoice(next);
    setReviewing(false);
    setAnnouncement(`You have ${next === 'granted' ? 'accepted' : 'rejected'} analytics and advertising cookies.`);
  };

  return (
    <>
      {/* Always present so assistive technology is already watching it when the text arrives. */}
      <p className="ConsentBanner__status" role="status" data-testid="ConsentBanner-status">
        {announcement}
      </p>
      {visible && (
        <section
          ref={bannerRef}
          className="ConsentBanner"
          aria-label="Cookies on Capacity Vault"
          data-testid="ConsentBanner"
        >
          <div className="ConsentBanner__inner">
            <div className="ConsentBanner__copy">
              <h2 ref={headingRef} tabIndex={-1}>Cookies on Capacity Vault</h2>
              <p>
                We use essential cookies to make this service work. With your permission we would
                also like to use cookies for analytics, so we can see how the service is used, and
                for measuring our advertising.{' '}
                <a href={`${LANDING_URL}/data-policy`} target="_blank" rel="noopener noreferrer">
                  Read our data policy
                </a>
                .
              </p>
            </div>
            <div className="ConsentBanner__actions">
              <Button variant="contained" color="primary" onClick={() => decide('granted')}>
                Accept cookies
              </Button>
              <Button variant="outlined" color="primary" onClick={() => decide('denied')}>
                Reject cookies
              </Button>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default ConsentBanner;
